import React, { useState, useEffect } from 'react';
import { FaFilter } from 'react-icons/fa';
import FraudAlertCard from '../components/fraud/FraudAlertCard';
import { supabase } from '../services/supabase';
import adminService from '../services/admin.service';
import { useAuth } from '../context/AuthContext';

function FraudPage() {
  const { admin } = useAuth();
  const [frauds, setFrauds] = useState([]);
  const [filteredFrauds, setFilteredFrauds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterResolved, setFilterResolved] = useState('unresolved');

  useEffect(() => {
    fetchFrauds();

    // Real-time subscription
    const subscription = supabase
      .channel('fraud_logs_changes')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'fraud_logs' },
        (payload) => {
          console.log('New fraud detected:', payload);
          fetchFrauds();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    filterFrauds();
  }, [filterSeverity, filterType, filterResolved, frauds]);

  const fetchFrauds = async () => {
    try {
      const { data, error } = await supabase
        .from('fraud_logs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // User və business məlumatlarını əlavə et
      const fraudsWithDetails = await Promise.all(
        data.map(async (fraud) => {
          let userData = null;
          let businessData = null;

          if (fraud.user_id) {
            const { data: user } = await supabase
              .from('users')
              .select('full_name, username, email')
              .eq('id', fraud.user_id)
              .single();
            userData = user;
          }

          if (fraud.business_id) {
            const { data: business } = await supabase
              .from('businesses')
              .select('name')
              .eq('id', fraud.business_id)
              .single();
            businessData = business;
          }

          return {
            ...fraud,
            user_name: userData?.full_name,
            user_username: userData?.username,
            user_email: userData?.email,
            business_name: businessData?.name,
          };
        })
      );

      setFrauds(fraudsWithDetails);
    } catch (error) {
      console.error('Error fetching frauds:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterFrauds = () => {
    let filtered = frauds;

    // Severity filter
    if (filterSeverity !== 'all') {
      filtered = filtered.filter((f) => f.severity === filterSeverity);
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter((f) => f.fraud_type === filterType);
    }

    // Resolved filter
    if (filterResolved === 'resolved') {
      filtered = filtered.filter((f) => f.resolved === true);
    } else if (filterResolved === 'unresolved') {
      filtered = filtered.filter((f) => f.resolved === false || !f.resolved);
    }

    setFilteredFrauds(filtered);
  };

  const handleViewDetails = (fraud) => {
    alert(`Fraud Details:\n\n${JSON.stringify(fraud, null, 2)}`);
  };

  const handleBanUser = async (fraud) => {
    if (!fraud.user_id) {
      alert('User ID tapılmadı!');
      return;
    }

    if (!window.confirm(`${fraud.user_name} fraud səbəbi ilə ban edilsin?`)) {
      return;
    }

    try {
      // Ban et
      const { error } = await supabase.from('user_bans').insert({
        user_id: fraud.user_id,
        reason: `Fraud: ${fraud.fraud_type}`,
        banned_by: admin.id,
      });

      if (error) throw error;

      // Fraud-u həll olunmuş kimi işarələ
      await supabase
        .from('fraud_logs')
        .update({ resolved: true })
        .eq('id', fraud.id);

      // Log yaz
      await adminService.logAction(
        admin.id,
        'ban_user_fraud',
        'user',
        fraud.user_id,
        { fraud_id: fraud.id }
      );

      alert('User uğurla ban edildi!');
      fetchFrauds();
    } catch (error) {
      console.error('Error banning user:', error);
      alert('Xəta baş verdi!');
    }
  };

  const handleResolve = async (fraud) => {
    if (!window.confirm('Bu fraud alert-i həll olunmuş kimi işarələyirsiniz?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('fraud_logs')
        .update({ resolved: true })
        .eq('id', fraud.id);

      if (error) throw error;

      // Log yaz
      await adminService.logAction(
        admin.id,
        'resolve_fraud',
        'fraud',
        fraud.id
      );

      alert('Fraud uğurla həll edildi!');
      fetchFrauds();
    } catch (error) {
      console.error('Error resolving fraud:', error);
      alert('Xəta baş verdi!');
    }
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>Yüklənir...</p>
      </div>
    );
  }

  const highPriority = filteredFrauds.filter((f) => f.severity === 'high' && !f.resolved);
  const mediumPriority = filteredFrauds.filter((f) => f.severity === 'medium' && !f.resolved);
  const lowPriority = filteredFrauds.filter((f) => f.severity === 'low' && !f.resolved);
  const resolved = filteredFrauds.filter((f) => f.resolved);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🚨 Fraud Detection Center</h1>
          <p style={styles.subtitle}>Şübhəli fəaliyyətləri izləyin və idarə edin</p>
        </div>
      </div>

      {/* Stats */}
      <div style={styles.stats}>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #F44336' }}>
          <span style={styles.statLabel}>YÜKSƏK:</span>
          <span style={{ ...styles.statValue, color: '#F44336' }}>
            {frauds.filter((f) => f.severity === 'high' && !f.resolved).length}
          </span>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #FF9800' }}>
          <span style={styles.statLabel}>ORTA:</span>
          <span style={{ ...styles.statValue, color: '#FF9800' }}>
            {frauds.filter((f) => f.severity === 'medium' && !f.resolved).length}
          </span>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #FFC107' }}>
          <span style={styles.statLabel}>AŞAĞI:</span>
          <span style={{ ...styles.statValue, color: '#FFC107' }}>
            {frauds.filter((f) => f.severity === 'low' && !f.resolved).length}
          </span>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #4CAF50' }}>
          <span style={styles.statLabel}>HƏLL OLUNDU:</span>
          <span style={{ ...styles.statValue, color: '#4CAF50' }}>
            {frauds.filter((f) => f.resolved).length}
          </span>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        <div style={styles.filterGroup}>
          <FaFilter style={styles.filterIcon} />
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">Bütün Severity</option>
            <option value="high">Yüksək</option>
            <option value="medium">Orta</option>
            <option value="low">Aşağı</option>
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">Bütün Tiplər</option>
            <option value="gps_spoofing">GPS Spoofing</option>
            <option value="multiple_qr">Multiple QR</option>
            <option value="rapid_checkins">Rapid Check-ins</option>
            <option value="fake_account">Fake Account</option>
            <option value="spin_abuse">Spin Abuse</option>
          </select>

          <select
            value={filterResolved}
            onChange={(e) => setFilterResolved(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">Hamısı</option>
            <option value="unresolved">Həll olunmamış</option>
            <option value="resolved">Həll olunmuş</option>
          </select>
        </div>
      </div>

      {/* Fraud Alerts */}
      {highPriority.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>🔴 YÜKSƏK PRİORİTET ({highPriority.length})</h3>
          {highPriority.map((fraud) => (
            <FraudAlertCard
              key={fraud.id}
              fraud={fraud}
              onViewDetails={handleViewDetails}
              onBanUser={handleBanUser}
              onResolve={handleResolve}
            />
          ))}
        </div>
      )}

      {mediumPriority.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>🟡 ORTA PRİORİTET ({mediumPriority.length})</h3>
          {mediumPriority.map((fraud) => (
            <FraudAlertCard
              key={fraud.id}
              fraud={fraud}
              onViewDetails={handleViewDetails}
              onBanUser={handleBanUser}
              onResolve={handleResolve}
            />
          ))}
        </div>
      )}

      {lowPriority.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>🟢 AŞAĞI PRİORİTET ({lowPriority.length})</h3>
          {lowPriority.map((fraud) => (
            <FraudAlertCard
              key={fraud.id}
              fraud={fraud}
              onViewDetails={handleViewDetails}
              onBanUser={handleBanUser}
              onResolve={handleResolve}
            />
          ))}
        </div>
      )}

      {filterResolved === 'all' && resolved.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>✅ HƏLL OLUNMUŞ ({resolved.length})</h3>
          {resolved.map((fraud) => (
            <FraudAlertCard
              key={fraud.id}
              fraud={fraud}
              onViewDetails={handleViewDetails}
              onBanUser={handleBanUser}
              onResolve={handleResolve}
            />
          ))}
        </div>
      )}

      {filteredFrauds.length === 0 && (
        <div style={styles.noData}>
          <p>🎉 Heç bir fraud alert yoxdur!</p>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '30px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#999',
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '30px',
  },
  statCard: {
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statLabel: {
    fontSize: '13px',
    color: '#666',
    fontWeight: '600',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: 'bold',
  },
  filters: {
    marginBottom: '30px',
  },
  filterGroup: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  filterIcon: {
    color: '#999',
    fontSize: '18px',
  },
  filterSelect: {
    padding: '12px 15px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: '#fff',
  },
  section: {
    marginBottom: '40px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '20px',
  },
  noData: {
    padding: '60px 20px',
    textAlign: 'center',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #FF6B35',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

export default FraudPage;